import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../stores/hook';
import language from '../../../../../utils/LanguageSettings';
import { resetHeight } from '../../../../functions/ResetHeight';
import { setDetailInfoId, setPackInfo, setPackInfoDetailStatus, setSubscribeModalStatus } from '../../GetStartedSlice';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CSSTransition } from 'react-transition-group';
import { A11y, Navigation, Pagination, Scrollbar } from 'swiper/modules';
import translations from '../../../../../text/get-started-translations.json';
import defaultImgBanner from '../../../../../images/icon/default-img-banner.png'

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/a11y';
import '../../../../../css/pack-list.scss'; 
import ImageFallback from '../../../../../utils/ImageFallback';
import { SOURCE_URL } from '../../../../../config';

interface IPackList {
  packs: any;
  genre: {
    key: string,
    label: string,
  };
  genreIsOpen: boolean | false;
}
const PackList:React.FC<IPackList> = (props) => {
  const pageTranslations = translations[language];
  const { packs, genre } = props;
  const genreRef = useRef<HTMLDivElement | null>(null);
  const [activeGenre, setActiveGenre] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(props.genreIsOpen);
  const nodeRef = useRef<HTMLDivElement | null> (null);
  const swiperRef = useRef<any>(null);
  const [scroll, setScroll] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 480);
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    resetHeight();
  }, []);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
    }
  }, [activeGenre]);

  useEffect(()=> {
    if(props.genreIsOpen) {
      setActiveGenre(genre.key);
    }
  },[])

  useEffect(() => {
    resetHeight();
    if (activeGenre && scroll) {
      if (genreRef.current) {
        const offsetTop = genreRef.current.offsetTop;
        scrollTo(offsetTop, 800);
      }
              
    }
  }, [activeGenre]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleClick = (id: string) => {
      dispatch(setDetailInfoId(id));
      dispatch(setPackInfoDetailStatus(true));
      setScroll(true);
  }
  
  const genreSwiperConfig = useMemo(() => {
    let config: any = {};
    const genrePacksLength = packs.length;
    switch(genrePacksLength) {
      case 1:
        config = {
          navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        modules: [Navigation, Pagination, Scrollbar, A11y],
        onSwiper: (swiper: any) => {
          swiperRef.current = swiper;
        },
      }
      break;
    case 2:
      config = {
        slidesPerView: 1.15,
        spaceBetween: 8,
        observer: true,
        observeParents: true,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
        breakpoints: {
          767: {
            slidesPerView: 2,
            spaceBetween: 10,
            allowTouchMove: false,
          },
          480: {
            slidesPerView: 2,
            spaceBetween: 8,
            allowTouchMove: true,
          }
        },
        modules: [Navigation, Pagination, Scrollbar, A11y],
        onSwiper: (swiper: any) => {
          swiperRef.current = swiper;
        },
      }
      break;
    case 3:
      config = {
        slidesPerView: 1.15,
        spaceBetween: 8,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        breakpoints: {
          991: {
            slidesPerView: 3,
            spaceBetween: 10,
            allowTouchMove: false,
          },
          767: {
            slidesPerView: 2.3,
            spaceBetween: 10,
            allowTouchMove: true,
          },
          480: {
            slidesPerView: 1.15,
            spaceBetween: 8,
            allowTouchMove: true,
          }
        },
        modules: [Navigation, Pagination, Scrollbar, A11y],
        onSwiper: (swiper: any) => {
          swiperRef.current = swiper;
        },
                    
      }
      break;
      default:
        config = {
          slidesPerView: 1.15,
          spaceBetween: 8,
          slidesPerGroup: 1,
          observer: true,
          observeParents: true,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          breakpoints: {
            991: {
              slidesPerView: 3.15,
              spaceBetween: 10,
              slidesPerGroup: 3
            },
            767: {
              slidesPerView: 2.3,
              spaceBetween: 10,
              slidesPerGroup: 2
            },
            480: {
              slidesPerView: 1.15,
              spaceBetween: 8,
              slidesPerGroup: 1
            }
          },
          modules: [Navigation, Pagination, Scrollbar, A11y],
          onSwiper: (swiper: any) => {
            swiperRef.current = swiper;
          },
        }
        break;
      }
        return config;        
    }, [activeGenre, packs, isSmallScreen])

  const scrollTo = (to: number, duration: number) => {
        const start = window.scrollY; const change = to - start;
        let startTime = 0;
        const animateScroll = (currentTime: number) => {
            if (startTime === 0) startTime = currentTime; 
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const easeInOutQuad = (progress: number) => progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
            window.scrollTo(0, start + change * easeInOutQuad(progress));
            if (timeElapsed < duration) {
                requestAnimationFrame(animateScroll);
            }
        }; 
        requestAnimationFrame(animateScroll);
    };

  const renderSwiperForGenre = useCallback((genrePacks: any) => {
    return (
      <div>
      { isSmallScreen ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {genrePacks.map((pack: any, index: number) => (
            <div key={index} style={{ marginBottom: '8px' }}>
              <div className="item slick-slide slick-current slick-active" >
                  <div className="h-100 advertisement-column hid-overflow mb-4 mx-sm-2 mx-0 text-center">
                    <div className="img-wrap">
                      <ImageFallback
                        primarySrc={pack.attributes.packImage?.data?.attributes?.url ? SOURCE_URL + pack.attributes.packImage?.data?.attributes?.url : ''}
                        defaultSrc={defaultImgBanner}
                        className='w-100 ls-is-cached lazyloaded'
                      />
                      <noscript>
                        <ImageFallback
                          primarySrc={pack.attributes.packImage?.data?.attributes?.url ? SOURCE_URL + pack.attributes.packImage?.data?.attributes?.url : ''}
                          defaultSrc={defaultImgBanner}
                          className='w-100'
                        />
                      </noscript>
                    </div>
                    <h4 className="text-20 px-3 py-4 font-medium">{pack.attributes.name}</h4>
                    <p className="mb-0 px-3 mb-4">
                      <span className="text-22 font-medium"></span>
                      <span className="text-16"></span>
                    </p>
                    <a onClick={() => handleClick(pack.id)} data-popup="22409" data-show-description="1" className="barlow-font color-1 arrowed-link mb-5">
                      {pageTranslations.SeeDetails}
                      <i className="icon-angle-right"></i>
                    </a>
                  </div>
                </div>
            </div>
          ))}
        </div>
      ) : (
        <Swiper key={genrePacks} {...genreSwiperConfig}>
          {genrePacks.map((pack: any, index: number) => (
              <SwiperSlide key={index} >
                  <div className="item slick-slide slick-current slick-active" >
                    <div className="h-100 advertisement-column hid-overflow mb-4 mx-sm-2 mx-0 text-center">
                      <div className="img-wrap">
                        <ImageFallback
                          primarySrc={pack.attributes.packImage?.data?.attributes?.url ? SOURCE_URL + pack.attributes.packImage?.data?.attributes?.url : ''}
                          defaultSrc={defaultImgBanner}
                          className='w-100 ls-is-cached lazyloaded'
                        />
                        <noscript>
                          <ImageFallback
                            primarySrc={pack.attributes.packImage?.data?.attributes?.url ? SOURCE_URL + pack.attributes.packImage?.data?.attributes?.url : ''}
                            defaultSrc={defaultImgBanner}
                            className='w-100'
                          />
                        </noscript>
                      </div>
                      <h4 className="text-20 px-3 py-4 font-medium">{pack.attributes.name}</h4>
                      <p className="mb-0 px-3 mb-4">
                        <span className="text-22 font-medium"></span>
                        <span className="text-16"></span>
                      </p>
                      <a onClick={() => handleClick(pack.id)} data-popup="22409" data-show-description="1" className="barlow-font color-1 arrowed-link mb-5">
                        {pageTranslations.SeeDetails}
                        <i className="icon-angle-right"></i>
                      </a>
                    </div>
                  </div>
              </SwiperSlide>
              ))
          }
          <div className="swiper-button-next"></div>
          <div className="swiper-button-prev"></div>
        </Swiper>
      )}
      </div>
    )
  }, [genre, packs, isSmallScreen])
  const handleGenreClick = (genre: string) => {

    setActiveGenre(activeGenre === genre ? null : genre);
    setIsOpen(!isOpen);
    setScroll(true);
  };  

  return (<>
    <div className={`accdn-item ${activeGenre === genre.key ? 'active' : ''}`} ref={genreRef}>
      <div>
        <h2 className={`accdn-head text-24 ${activeGenre === genre.key ? 'active' : ''}`} onClick={() => handleGenreClick(genre.key)}>{genre.label}</h2>
      </div>
        <CSSTransition
          nodeRef={nodeRef}
          in={isOpen}
          timeout={200}
          unmountOnExit
          classNames="genre-slide" >
          <div ref={nodeRef} className="combo-carousel combo-04 slick-initialized slick-slider">
            {renderSwiperForGenre(packs)}
          </div>
          </CSSTransition>   
        </div>
  </>)
}

export default PackList;